import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reports, projects } from '../services/api';
import BarList from '../components/BarList';

const STATUS = {
  done:            { label: '✓ Done',      color: 'text-good',   bg: 'bg-good/10',   dot: 'bg-good',    track: 'bg-good/15' },
  syncing:         { label: '⟳ Syncing',   color: 'text-warn',   bg: 'bg-warn/10',   dot: 'bg-warn',    track: 'bg-warn/15' },
  failed:          { label: '✗ Failed',    color: 'text-bad',    bg: 'bg-bad/10',    dot: 'bg-bad',     track: 'bg-bad/15' },
  pending:         { label: '● Pending',   color: 'text-muted',  bg: 'bg-muted/10',  dot: 'bg-muted',   track: 'bg-muted/15' },
  resetting:       { label: '↺ Resetting', color: 'text-special', bg: 'bg-special/10', dot: 'bg-special', track: 'bg-special/15' },
  waiting_restore: { label: '⏳ Waiting',   color: 'text-accent', bg: 'bg-accent/10', dot: 'bg-accent',  track: 'bg-accent/15' },
  stopped:         { label: '■ Stopped',   color: 'text-dim',    bg: 'bg-dim/10',    dot: 'bg-dim',     track: 'bg-dim/15' },
};
const statusMeta = s => STATUS[s] || STATUS.pending;

export default function Reports() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([projects.get(id), reports.get(id)])
      .then(([p, r]) => { setProject(p.data); setData(r.data); })
      .finally(() => setLoading(false));
  }, [id]);

  const formatGiB = b => b > 0 ? (b / 1073741824).toFixed(2) + ' GiB' : '—';
  const formatGiBRaw = b => (b / 1073741824).toFixed(2) + ' GiB';

  const accountsByStatus = Object.entries(
    (data?.accounts || []).reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] || 0) + 1 }), {})
  )
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => {
      const meta = statusMeta(status);
      return { key: status, label: meta.label, value: count, display: String(count), color: meta.dot, track: meta.track };
    });

  const TOP_N = 8;
  const sortedByBytes = [...(data?.accounts || [])].sort((a, b) => (b.bytes_transferred || 0) - (a.bytes_transferred || 0));
  const topByTransfer = sortedByBytes.slice(0, TOP_N).map(a => ({
    key: a.email, label: a.username, value: a.bytes_transferred || 0,
    display: formatGiBRaw(a.bytes_transferred || 0), color: 'bg-accent', track: 'bg-accent/15',
  }));
  const formatDuration = (start, end) => {
    if (!start || !end) return '—';
    const secs = Math.round((new Date(end) - new Date(start)) / 1000);
    const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const downloadCSV = () => {
    if (!data?.accounts) return;
    const headers = ['Username', 'Email', 'Status', 'Messages', 'GiB Transferred', 'Speed (KiB/s)', 'Errors', 'Duration'];
    const rows = data.accounts.map(a => [
      a.username, a.email, a.status, a.messages_synced,
      (a.bytes_transferred / 1073741824).toFixed(2),
      a.speed_kbps, a.errors,
      formatDuration(a.started_at, a.completed_at)
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `migration-report-${project?.name || id}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-10 font-sans text-muted">Loading…</div>;

  return (
    <div className="min-h-screen bg-bg font-sans text-ink">
      {/* Topbar */}
      <div className="sticky top-0 z-10 flex h-14 items-center gap-4 border-b border-border2 bg-bg px-6">
        <button onClick={() => navigate(`/projects/${id}`)}
          className="rounded-md border border-border2 px-3 py-1.5 text-xs text-muted transition-colors hover:text-ink">
          ← Back
        </button>
        <div className="flex-1 font-display text-[15px] font-bold">
          Migration Report · {project?.name}
        </div>
        <button onClick={downloadCSV}
          className="rounded-md bg-good px-4 py-1.5 text-sm font-semibold text-white shadow-[0_0_16px_rgba(34,201,138,.2)] transition-opacity hover:opacity-90">
          ↓ Export CSV
        </button>
      </div>

      <div className="mx-auto max-w-[1100px] p-6">
        {/* Summary cards */}
        <div className="mb-7 grid grid-cols-4 gap-3">
          {[
            ['Accounts Done', `${data?.summary?.done || 0} / ${data?.summary?.total || 0}`, 'text-good'],
            ['Total Messages', (data?.summary?.total_messages || 0).toLocaleString(), 'text-accent'],
            ['Data Transferred', formatGiB(data?.summary?.total_bytes || 0), 'text-warn'],
            ['Total Errors', data?.summary?.total_errors || 0, data?.summary?.total_errors > 0 ? 'text-bad' : 'text-good'],
          ].map(([label, val, color]) => (
            <div key={label} className="rounded-xl border border-border2 bg-surface px-5 py-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-dim">{label}</div>
              <div className={`font-mono text-2xl font-bold ${color}`}>{val}</div>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mb-6 rounded-xl border border-border2 bg-surface px-5 py-4">
          <div className="mb-2.5 flex justify-between text-sm">
            <span className="font-semibold">Overall Progress</span>
            <span className="font-mono text-muted">{data?.summary?.done || 0} / {data?.summary?.total || 0} accounts</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-border2">
            <div className="h-full rounded-full bg-good transition-all duration-500"
              style={{ width: data?.summary?.total > 0 ? `${((data?.summary?.done || 0) / data?.summary?.total) * 100}%` : '0%' }} />
          </div>
        </div>

        {/* Charts */}
        <div className="mb-6 grid grid-cols-2 gap-3">
          <BarList title="Accounts by status" rows={accountsByStatus} />
          <BarList title="Top accounts by data transferred" rows={topByTransfer}
            footnote={sortedByBytes.length > TOP_N ? `+${sortedByBytes.length - TOP_N} more in the table below` : undefined} />
        </div>

        {/* Accounts table */}
        <div className="overflow-hidden rounded-xl border border-border2 bg-surface">
          <div className="flex items-center justify-between border-b border-border2 px-5 py-3.5">
            <span className="text-sm font-bold">Per-account breakdown</span>
            <span className="font-mono text-xs text-dim">{data?.accounts?.length || 0} accounts</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse">
              <thead>
                <tr>
                  {['Account', 'Status', 'Messages', 'Data', 'Speed', 'Errors', 'Duration'].map(h => (
                    <th key={h} className="border-b border-border2 bg-surface px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-dim">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.accounts?.map(a => {
                  const meta = statusMeta(a.status);
                  return (
                    <tr key={a.email} className="hover:bg-surface2/50">
                      <td className="border-b border-border/60 px-3.5 py-3 font-mono text-xs">{a.email}</td>
                      <td className="border-b border-border/60 px-3.5 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.color} ${meta.bg}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="border-b border-border/60 px-3.5 py-3 font-mono text-xs">{(a.messages_synced || 0).toLocaleString()}</td>
                      <td className="border-b border-border/60 px-3.5 py-3 font-mono text-xs">{formatGiB(a.bytes_transferred || 0)}</td>
                      <td className="border-b border-border/60 px-3.5 py-3 font-mono text-xs text-muted">{a.speed_kbps ? `${a.speed_kbps} KiB/s` : '—'}</td>
                      <td className={`border-b border-border/60 px-3.5 py-3 font-mono text-xs ${a.errors > 0 ? 'text-bad' : 'text-good'}`}>{a.errors ?? '—'}</td>
                      <td className="border-b border-border/60 px-3.5 py-3 font-mono text-xs text-muted">{formatDuration(a.started_at, a.completed_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
